import linkifyHtml from 'linkify-html';
import { isSafeHref } from './paste-sanitize';

const ALL_TAGS_RE = /(<[^>]*>)/g;
const HASHTAG_RE = /#([a-zA-Z][a-zA-Z0-9_]{0,49})\b/g;
// ASCII hyphen-minus (U+002D) at the end of the class — U+2010 looks the same
// and made `@maya-upgrade-qa` capture only `maya` because `\b` fired at `-`.
const MENTION_RE = /@([a-zA-Z0-9_.-]{1,100})\b/g;
const ANCHOR_OPEN_RE = /<a\b([^>]*)>/gi;
const REQUIRED_REL_TOKENS = ['nofollow', 'noopener', 'noreferrer'] as const;

const LINKIFY_OPTIONS = {
  defaultProtocol: 'https' as const,
  target: '_blank',
  rel: 'nofollow noopener noreferrer',
  ignoreTags: ['a', 'code', 'pre', 'script', 'style'],
};

/** Mirrors CommunityEditor bare-domain paste: scheme-less host-like values only. */
const SCHEMELESS_HOST_LIKE_HREF_RE = /^(?:www\.)?[\w-]+(?:\.[\w-]{2,})+(?:\/\S*)?$/i;

function normalizeHrefForSanitize(href: string): string {
  const trimmed = href.replace(/\s/g, '');
  if (isSafeHref(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return trimmed;
  }
  if (SCHEMELESS_HOST_LIKE_HREF_RE.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

/**
 * Replaces any `href` attribute value inside an HTML tag whose scheme is not
 * in the safe allowlist with `#`, so that stored javascript:, data:, or
 * vbscript: links cannot execute when rendered via dangerouslySetInnerHTML.
 * Scheme-less host-like hrefs (e.g. `www.instagram.com/...`) are upgraded to
 * `https://` before the safety check.
 */
function sanitizeLinkHrefs(html: string): string {
  return html.replace(/<[a-zA-Z][^>]*>/g, (tag) =>
    tag.replace(/\bhref\s*=\s*(?:(["'])([^"']*)\1|([^\s"'`=<>]+))/gi, (_m, q, quotedHref, unquotedHref) => {
      const normalizedHref = normalizeHrefForSanitize(q ? quotedHref : unquotedHref);
      if (q) {
        return isSafeHref(normalizedHref) ? `href=${q}${normalizedHref}${q}` : `href=${q}#${q}`;
      }
      return isSafeHref(normalizedHref) ? `href="${normalizedHref}"` : `href="#"`;
    })
  );
}

function tagHashtagsAndMentions(html: string): string {
  let anchorDepth = 0;
  let taggedSpanDepth = 0;
  let innerSpanDepth = 0;

  return html
    .split(ALL_TAGS_RE)
    .map((part, i) => {
      if (i % 2 === 1) {
        if (/^<a[\s>]/i.test(part)) anchorDepth++;
        else if (/^<\/a\s*>/i.test(part)) anchorDepth = Math.max(0, anchorDepth - 1);

        if (/^<span\b[^>]*\b(?:data-hashtag|data-mention)\b/i.test(part)) {
          taggedSpanDepth++;
        } else if (/^<span\b/i.test(part)) {
          if (taggedSpanDepth > 0) innerSpanDepth++;
        } else if (/^<\/span\s*>/i.test(part)) {
          if (innerSpanDepth > 0) innerSpanDepth--;
          else if (taggedSpanDepth > 0) taggedSpanDepth--;
        }

        return part;
      }

      if (anchorDepth > 0 || taggedSpanDepth > 0) return part;

      return part
        .replace(HASHTAG_RE, '<span data-hashtag="" data-name="$1">#$1</span>')
        .replace(MENTION_RE, '<span data-mention="" data-slug="$1">@$1</span>');
    })
    .join('');
}

function mergeRelTokens(existing: string): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const token of existing.split(/\s+/).filter(Boolean)) {
    const key = token.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(token);
    }
  }
  for (const token of REQUIRED_REL_TOKENS) {
    if (!seen.has(token)) {
      seen.add(token);
      out.push(token);
    }
  }
  return out.join(' ');
}

function ensureAnchorRel(html: string): string {
  return html.replace(ANCHOR_OPEN_RE, (_match, attrs: string) => {
    let next = attrs;

    const relMatch = next.match(/\brel\s*=\s*(["'])([^"']*)\1/i);
    const mergedRel = mergeRelTokens(relMatch?.[2] ?? '');
    if (relMatch) {
      next = next.replace(/\brel\s*=\s*(["'])[^"']*\1/i, `rel="${mergedRel}"`);
    } else {
      next = `${next} rel="${mergedRel}"`;
    }

    if (/\btarget\s*=/i.test(next)) {
      next = next.replace(/\btarget\s*=\s*(?:(["'])[^"']*\1|[^\s>]+)/i, 'target="_blank"');
    } else {
      next = `${next} target="_blank"`;
    }

    return `<a${next}>`;
  });
}

/**
 * Plain text into the HTML the community-comment column stores.
 *
 * Escaped first, then wrapped in paragraphs — the reader's own angle brackets
 * are content, not markup. Town-square `renderCommentHtml` treats a payload
 * that starts with `<` as HTML and only rewrites hrefs, so a raw textarea
 * string that begins with `<img` would execute in every viewer.
 */
export function plainTextToCommentHtml(text: string): string {
  const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  return escaped
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br />')}</p>`)
    .join('');
}

/**
 * Render-time pipeline for a PLAIN TEXT body — the critique composer is a
 * <textarea>, not Tiptap.
 *
 * Two things a comment gets for free that a critique did not:
 *   · bare URLs become links (Tiptap or linkifyHtml did it)
 *   · a link can carry READABLE TEXT rather than showing a raw URL, because the
 *     Tiptap composer stores a real anchor
 *
 * A textarea can express the second only as `[label](https://example.com)`, so
 * that is converted here, after escaping and before the shared pipeline. The
 * label is already escaped by the time it is spliced in, and the href must pass
 * isSafeHref, so `javascript:` and friends degrade to plain text rather than
 * becoming a live anchor.
 */
export function renderPlainTextBodyHtml(text: string): string {
  if (!text) return text;
  return processBodyContentHtml(markdownLinksToAnchors(plainTextToCommentHtml(text)));
}

/**
 * `[label](href)` -> `<a href="href">label</a>`, over ALREADY-ESCAPED text.
 *
 * Deliberately not a markdown parser: one pattern, no nesting, no emphasis, no
 * images. Anything that does not match is left exactly as the artist typed it.
 */
function markdownLinksToAnchors(escapedHtml: string): string {
  return escapedHtml.replace(/\[([^\]\n]+)\]\((https?:\/\/(?:[^\s()]|\([^)\s]*\))+)\)/g, (whole, label, href) => {
    if (!isSafeHref(href)) return whole;
    return `<a href="${href}">${label}</a>`;
  });
}

/**
 * Render-time pipeline for community post/comment HTML: sanitize unsafe hrefs,
 * linkify bare URLs and emails, wrap hashtags/mentions, normalize anchor rel/target.
 */
export function processBodyContentHtml(html: string): string {
  if (!html) return html;
  const sanitized = sanitizeLinkHrefs(html);
  const linked = linkifyHtml(sanitized, LINKIFY_OPTIONS);
  const tagged = tagHashtagsAndMentions(linked);
  return ensureAnchorRel(tagged);
}

/**
 * Replaces images whose data-social-image-id is in nsfwIds with an inline HTML
 * placeholder. Used for comment/post HTML rendered via dangerouslySetInnerHTML,
 * where React components cannot be injected. The nsfwImageIds list comes from
 * the API; the placeholder indicates the image has been flagged without loading
 * any image bytes.
 */
const DEFAULT_FLAGGED_PLACEHOLDER =
  "This image has been flagged as potentially violating ArtHelper's policies and is pending staff review";

export function stripNsfwImages(
  html: string,
  nsfwIds: string[],
  placeholderText?: string,
  learnMoreLink?: { href: string; text: string }
): string {
  if (!html || nsfwIds.length === 0) return html;
  const nsfwSet = new Set(nsfwIds);
  const label = placeholderText ?? DEFAULT_FLAGGED_PLACEHOLDER;
  const linkHtml = learnMoreLink
    ? `<a href="${learnMoreLink.href}" style="margin-left:22px;color:#4f46e5;text-decoration:none;white-space:nowrap;margin-top:4px" onclick="event.stopPropagation()">${learnMoreLink.text} \u00BB</a>`
    : '';
  return html.replace(/<img([^>]*?)data-social-image-id="([^"]+)"[^>]*>/g, (match, _before, imageId) => {
    if (!nsfwSet.has(imageId)) return match;
    return (
      `<span data-nsfw-placeholder="${imageId}" ` +
      `style="display:flex;flex-direction:column;gap:0;padding:10px 14px;background:#f3f4f6;border-radius:6px;` +
      `max-width:300px;color:#6b7280;font-size:0.8125rem;border:1px solid #e5e7eb">` +
      `<span style="display:flex;align-items:center;gap:8px">` +
      `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><path d="m2 2 20 20"/><path d="M5 5a1 1 0 0 0-1 1v7c0 5 3.5 7.5 7.67 8.94a1 1 0 0 0 .67.01c2.35-.82 4.48-1.97 5.9-3.71"/><path d="M9.309 3.652A12.252 12.252 0 0 0 11.24 2.28a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1v7a9.784 9.784 0 0 1-.08 1.264"/></svg>` +
      `${label}</span>` +
      `${linkHtml}` +
      `</span>`
    );
  });
}

/**
 * Wraps images whose data-social-image-id is in nsfwIds with a blur overlay.
 *
 * Layout:
 *  - Center of overlay: "Sensitive" icon + label (pointer-events:none, decorative only)
 *  - Bottom-center of container: a persistent show/hide toggle button
 *
 * The button always stays visible at the bottom of the image — it reads "Show image"
 * when blurred and "Hide image" when revealed.  Use handleNsfwContainerClick as the
 * onClick handler on the parent container to drive all NSFW interactions.
 */
export function blurNsfwImages(
  html: string,
  nsfwIds: string[],
  options?: { showLabel?: string; hideLabel?: string }
): string {
  if (!html || nsfwIds.length === 0) return html;
  const nsfwSet = new Set(nsfwIds);
  const showLabel = options?.showLabel ?? 'Show image';
  return html.replace(/<img([^>]*?)data-social-image-id="([^"]+)"([^>]*)>/g, (match, before, imageId, after) => {
    if (!nsfwSet.has(imageId)) return match;
    // Strip any existing style attributes from both captured groups so we don't
    // end up with two style= attributes (browsers honour the first, ignoring ours).
    const cleanBefore = before.replace(/\s*style="[^"]*"/g, '');
    const cleanAfter = after.replace(/\s*style="[^"]*"/g, '');
    return (
      // Container — sizes to the image, no extra cursor since the button is the affordance
      `<span data-nsfw-container="${imageId}" style="position:relative;display:block;width:fit-content;max-width:100%;border-radius:0.375rem;overflow:hidden">` +
      // The image — starts blurred; no transition on initial render so the browser
      // applies the filter immediately rather than animating from filter:none.
      // will-change:filter puts the image on its own compositing layer so the
      // GPU applies the filter before the first paint, preventing the "dark
      // overlay but unblurred image" flash when a parent dialog is animating.
      `<img${cleanBefore}data-social-image-id="${imageId}"${cleanAfter} style="filter:blur(20px);will-change:filter;max-width:100%;height:auto;display:block;margin:0">` +
      // Semi-transparent overlay — "Sensitive" label centered, pointer-events:none so the button beneath shows through
      `<span data-nsfw-overlay="${imageId}" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.4);pointer-events:none">` +
      `<span style="display:flex;align-items:center;gap:4px;color:white;font-size:0.75rem">` +
      `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m2 2 20 20"/><path d="M5 5a1 1 0 0 0-1 1v7c0 5 3.5 7.5 7.67 8.94a1 1 0 0 0 .67.01c2.35-.82 4.48-1.97 5.9-3.71"/><path d="M9.309 3.652A12.252 12.252 0 0 0 11.24 2.28a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1v7a9.784 9.784 0 0 1-.08 1.264"/></svg>` +
      `Sensitive` +
      `</span>` +
      `</span>` +
      // Toggle button — top-center, always visible so tall images don't push it out of view
      `<button data-nsfw-toggle="${imageId}" type="button" style="position:absolute;top:8px;left:50%;transform:translateX(-50%);padding:3px 10px;border-radius:9999px;background:rgba(255,255,255,0.9);color:#111827;font-size:0.7rem;font-weight:500;border:none;cursor:pointer;white-space:nowrap;z-index:1">${showLabel}</button>` +
      `</span>`
    );
  });
}

/**
 * Event-delegation click handler for any container that renders HTML produced by
 * blurNsfwImages.  Attach it as the onClick of a wrapping div.
 *
 * Behaviour:
 *  - Click on [data-nsfw-toggle] button  → toggle blur on the image + toggle overlay
 *    visibility + update button label.
 *  - Click on a revealed <img> inside [data-nsfw-container] → call onImageReveal(src)
 *    so the parent can open a lightbox.
 *
 * Returns true if the click was consumed by an NSFW element (so callers can bail out
 * of their own click handlers).
 *
 * Compatible with both native MouseEvent and React's SyntheticEvent.
 */
export function handleNsfwContainerClick(
  e: { target: EventTarget | null; stopPropagation(): void },
  onImageReveal?: (src: string) => void,
  options?: { showLabel?: string; hideLabel?: string }
): boolean {
  const target = e.target as HTMLElement;

  // ── Toggle button ────────────────────────────────────────────────────────────
  const toggleBtn = target.closest('[data-nsfw-toggle]') as HTMLButtonElement | null;
  if (toggleBtn) {
    const container = toggleBtn.closest('[data-nsfw-container]') as HTMLElement | null;
    if (container) {
      const img = container.querySelector('img') as HTMLImageElement | null;
      const overlay = container.querySelector('[data-nsfw-overlay]') as HTMLElement | null;
      if (img && overlay) {
        const isRevealed = img.style.filter === 'none';
        // Add transition only for user-initiated toggles, not on initial render.
        img.style.transition = 'filter 0.3s';
        if (isRevealed) {
          img.style.filter = 'blur(20px)';
          overlay.style.display = 'flex';
          toggleBtn.textContent = options?.showLabel ?? 'Show image';
        } else {
          img.style.filter = 'none';
          overlay.style.display = 'none';
          toggleBtn.textContent = options?.hideLabel ?? 'Hide image';
        }
      }
    }
    e.stopPropagation();
    return true;
  }

  // ── Revealed image click → lightbox ──────────────────────────────────────────
  if (target.tagName === 'IMG') {
    const container = target.closest('[data-nsfw-container]') as HTMLElement | null;
    if (container) {
      const img = target as HTMLImageElement;
      if (img.style.filter === 'none' && onImageReveal) {
        onImageReveal(img.src);
      }
      // Always consume so the parent doesn't also fire
      return true;
    }
  }

  return false;
}
